/**
 * A select whose list can be themed.
 *
 * A native <select> renders its option list in the operating system's own layer.
 * No stylesheet reaches inside it, which is why the list stayed grey-on-blue
 * while everything around it became sakura pink. The only way to theme the list
 * is to stop using the platform control for it.
 *
 * That trade is not free - a native select brings keyboard support, screen
 * reader semantics and mobile pickers for nothing - so the listbox pattern is
 * implemented here in full rather than approximated:
 *
 *   - a combobox button that owns the value and the popup's expanded state
 *   - a listbox popup with roving `aria-activedescendant`, not focus per option
 *   - arrow keys, Home, End, Enter, Space, Escape, Tab, and click-outside
 *   - type-ahead: typing jumps to the option that starts that way
 *   - the active option scrolled into view, so arrowing past the fold works
 *   - focus returned to the button on close, always
 *
 * The popup is portalled out of the card it belongs to. Every card in this
 * application sets `overflow: hidden` to clip its corner flower, and an inline
 * popup inside one is cut off at the card's edge. Where it goes is decided by
 * `popupHost`, because a dialog is in the browser's top layer and nothing in
 * <body> can paint over it - see popup-host.ts.
 */
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { popupHost, popupOrigin } from '../popup-host.ts';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface Position {
  left: number;
  top: number;
  width: number;
  /** Set when the popup would run past the bottom of the viewport. */
  above: boolean;
}

export function Select({
  id,
  value,
  options,
  onChange,
  disabled,
  placeholder = '選択してください',
  className,
  ariaLabel,
}: {
  id?: string;
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  /** Extra classes on the button, for callers that size it themselves. */
  className?: string;
  ariaLabel?: string;
}) {
  const listId = useId();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<Position | null>(null);
  const selectedIndex = options.findIndex((option) => option.value === value);
  /** The option the keyboard is on. Not the same as the selected one. */
  const [activeIndex, setActiveIndex] = useState(selectedIndex < 0 ? 0 : selectedIndex);

  const selected = options[selectedIndex];

  const measure = useCallback(() => {
    const trigger = buttonRef.current;
    if (!trigger) return;
    const box = trigger.getBoundingClientRect();
    // Zero unless the host establishes a containing block; see popupOrigin.
    const origin = popupOrigin(popupHost(trigger));
    // 260px is the popup's max height; flipping above avoids a list that runs
    // off the bottom of a short window.
    const room = window.innerHeight - box.bottom;
    setPosition({
      left: box.left - origin.left,
      top: (room < 260 && box.top > room ? box.top : box.bottom + 4) - origin.top,
      width: box.width,
      above: room < 260 && box.top > room,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    measure();
    // Track the trigger while the page moves under it. `capture` catches scrolls
    // in any ancestor, not just the window.
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [open, measure]);

  /*
   * Focus the popup once it exists.
   *
   * Not on `open` alone: the popup renders only after `position` has been
   * measured, which is a second render. On the first one the ref is still null,
   * the focus call is a no-op, and the keyboard has nowhere to go - the list
   * opens and arrow keys do nothing. The guard keeps it to once per open, so a
   * reposition on scroll does not steal focus back.
   */
  const focusedForOpen = useRef(false);
  useEffect(() => {
    if (!open) {
      focusedForOpen.current = false;
      return;
    }
    if (position && !focusedForOpen.current) {
      listRef.current?.focus();
      focusedForOpen.current = true;
    }
  }, [open, position]);

  /*
   * Keep the keyboard's option on screen.
   *
   * The list caps at 260px and scrolls, and nothing was scrolling it: arrowing
   * down a plan list moved an `aria-activedescendant` nobody could see, and the
   * list appeared to stop responding at the sixth option. `nearest` so it
   * scrolls by the one row needed rather than recentring the list under the
   * pointer on every keypress.
   */
  useEffect(() => {
    if (!open) return;
    const list = listRef.current;
    const active = list?.querySelector<HTMLElement>(`#${CSS.escape(`${listId}-${activeIndex}`)}`);
    active?.scrollIntoView({ block: 'nearest' });
  }, [open, activeIndex, listId]);

  /*
   * Type-ahead, which a native <select> gives for free and this had to lose.
   *
   * Typed letters accumulate for a second and jump to the first option starting
   * with them, so 「ス」「タ」 reaches スタータープラン without arrowing past
   * everything above it. The buffer clears on its own; holding one key repeats
   * that letter, which is the platform behaviour for cycling same-letter
   * options and is why a repeated single character searches by first letter
   * instead of appending.
   */
  const typed = useRef({ text: '', at: 0 });
  const typeAhead = useCallback(
    (key: string) => {
      const now = Date.now();
      const buffer = now - typed.current.at > 1000 ? key : typed.current.text + key;
      typed.current = { text: buffer, at: now };

      const query = buffer.split('').every((char) => char === buffer[0]) ? buffer[0]! : buffer;
      const from = query === buffer ? 0 : activeIndex + 1;
      const found = options.findIndex(
        (option, index) =>
          index >= from && !option.disabled && option.label.toLowerCase().startsWith(query.toLowerCase()),
      );
      const wrapped =
        found >= 0
          ? found
          : options.findIndex(
              (option) =>
                !option.disabled && option.label.toLowerCase().startsWith(query.toLowerCase()),
            );
      if (wrapped >= 0) setActiveIndex(wrapped);
    },
    [options, activeIndex],
  );

  // Pointer down rather than click: closing on click would fire after a control
  // underneath had already been activated through the open popup.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (buttonRef.current?.contains(target) || listRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  const close = (focusTrigger = true) => {
    setOpen(false);
    if (focusTrigger) buttonRef.current?.focus();
  };

  const commit = (index: number) => {
    const option = options[index];
    if (!option || option.disabled) return;
    onChange(option.value);
    close();
  };

  const step = (from: number, delta: number): number => {
    let next = from;
    for (let i = 0; i < options.length; i += 1) {
      next = (next + delta + options.length) % options.length;
      if (!options[next]?.disabled) return next;
    }
    return from;
  };

  function onTriggerKeyDown(event: React.KeyboardEvent) {
    if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
      event.preventDefault();
      setActiveIndex(selectedIndex < 0 ? 0 : selectedIndex);
      setOpen(true);
    }
  }

  function onListKeyDown(event: React.KeyboardEvent) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setActiveIndex((current) => step(current, 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActiveIndex((current) => step(current, -1));
        break;
      case 'Home':
        event.preventDefault();
        setActiveIndex(step(-1, 1));
        break;
      case 'End':
        event.preventDefault();
        setActiveIndex(step(0, -1));
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        commit(activeIndex);
        break;
      case 'Escape':
        event.preventDefault();
        close();
        break;
      case 'Tab':
        // Let focus leave, but do not leave a popup floating behind it.
        close(false);
        break;
      default:
        // A single printable character is a search, not a shortcut. Modifiers
        // are left alone so browser and screen-reader commands still work.
        if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
          event.preventDefault();
          typeAhead(event.key);
        }
        break;
    }
  }

  return (
    <>
      <button
        ref={buttonRef}
        id={id}
        type="button"
        className={`selectbox${className ? ` ${className}` : ''}`}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
        disabled={disabled}
        onClick={() => {
          setActiveIndex(selectedIndex < 0 ? 0 : selectedIndex);
          setOpen((wasOpen) => !wasOpen);
        }}
        onKeyDown={onTriggerKeyDown}
      >
        <span className={`selectbox__value${selected ? '' : ' selectbox__value--empty'}`}>
          {selected?.label ?? placeholder}
        </span>
        <svg
          className="selectbox__caret"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {open &&
        position &&
        createPortal(
          <div
            ref={listRef}
            id={listId}
            role="listbox"
            tabIndex={-1}
            className="selectlist"
            aria-activedescendant={`${listId}-${activeIndex}`}
            onKeyDown={onListKeyDown}
            style={{
              left: position.left,
              width: Math.max(position.width, 160),
              ...(position.above
                ? { bottom: window.innerHeight - position.top + 4 }
                : { top: position.top }),
            }}
          >
            {options.map((option, index) => (
              <div
                key={option.value}
                id={`${listId}-${index}`}
                role="option"
                aria-selected={option.value === value}
                aria-disabled={option.disabled}
                className={`selectlist__option${
                  index === activeIndex ? ' selectlist__option--active' : ''
                }${option.value === value ? ' selectlist__option--selected' : ''}`}
                // Mouse move rather than hover in CSS: the keyboard's active
                // option and the pointer's must be the same one, or arrowing
                // after a hover appears to jump.
                onMouseMove={() => setActiveIndex(index)}
                onClick={() => commit(index)}
              >
                <span className="selectlist__label">{option.label}</span>
                {option.value === value && (
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                  >
                    <path d="M5 12.5l4.5 4.5L19 7" />
                  </svg>
                )}
              </div>
            ))}
          </div>,
          popupHost(buttonRef.current),
        )}
    </>
  );
}
